<template>
	<component :is="tag" :class="look">
		<slot/>
	</component>
</template>

<script setup lang="ts">
import {computed} from "vue";

/**
 * A heading whose level is chosen by the editor, independently of how big it should look: the level (h1-h6) says what
 * the heading is in the structure of the page, the look stays what the component had. Without a level (or with the
 * level the component used so far) nothing changes: the same tag with the same look as before.
 *
 * `default-level`: the level the component used before this setting. `plain`: the text had no heading look at all
 * (it is text in a box): a chosen level changes the tag only, the text looks the same as before.
 */
const props = defineProps({
	level: {
		type: [Number, String],
		default: null,
	},
	defaultLevel: {
		type: Number,
		default: 3,
	},
	plain: {
		type: Boolean,
		default: false,
	},
});

const chosen = computed((): number | null => {
	const level = Number(props.level);

	return Number.isInteger(level) && level >= 1 && level <= 6 ? level : null;
});

const tag = computed(() => chosen.value ? 'h' + chosen.value : (props.plain ? 'div' : 'h' + props.defaultLevel));

// The look of the level that the component used before (the global style of that heading level)
const look = computed(() => {
	if (!chosen.value) {
		return undefined;
	}

	return props.plain ? 'heading-look-plain' : (chosen.value === props.defaultLevel ? undefined : 'heading-look-h' + props.defaultLevel);
});
</script>
